import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'
import { useAuth } from '../auth/AuthContext.jsx'

// Admin: presences report. Filter by course and/or user over a date range
// (default: the last month). Stats (total presences, distinct participants,
// distinct courses) sit above the filters and the list.

// Local YYYY-MM-DD (avoid toISOString's UTC shift near midnight).
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
function lastCalendarMonth() {
  const now = new Date()
  // Previous calendar month: 1st … last day.
  const from = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  const to = new Date(now.getFullYear(), now.getMonth(), 0)
  return { from: iso(from), to: iso(to) }
}
function last30Days() {
  const to = new Date()
  const from = new Date()
  from.setDate(from.getDate() - 30)
  return { from: iso(from), to: iso(to) }
}

export function AdminPresencesPage() {
  const { t } = useTranslation()
  const { user, loading: authLoading } = useAuth()

  const [courses, setCourses] = useState([])
  const [users, setUsers] = useState([])
  const [courseId, setCourseId] = useState('')
  const [userId, setUserId] = useState('')
  const [range, setRange] = useState(lastCalendarMonth)
  const [rows, setRows] = useState([])
  const [stats, setStats] = useState({ total_presences: 0, distinct_participants: 0, distinct_courses: 0 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Dropdown data (courses with presences = bbso hourly/sessions; all users).
  useEffect(() => {
    if (authLoading || !user || user.role !== 'admin') return
    Promise.all([api.admin.courses.list(), api.admin.users.list()])
      .then(([c, u]) => {
        setCourses((c.courses || []).filter(
          (x) => x.managed === 'bbso' && (x.type === 'hourly' || x.type === 'sessions'),
        ))
        setUsers(u.users || [])
      })
      .catch((e) => setError(e.code || 'server'))
  }, [authLoading, user])

  function load() {
    setLoading(true); setError(null)
    api.admin.presences.list({
      course_id: courseId || undefined,
      user_id: userId || undefined,
      from: range.from,
      to: range.to,
    })
      .then((d) => { setRows(d.presences || []); setStats(d.stats || {}) })
      .catch((e) => setError(e.code || 'server'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (authLoading || !user || user.role !== 'admin') return
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, courseId, userId, range.from, range.to])

  if (authLoading) return <main className="container"><p>{t('common.loading')}</p></main>
  if (!user || user.role !== 'admin') {
    return <main className="container"><p className="error">{t('admin.errors.not_admin')}</p></main>
  }

  return (
    <main className="container">
      <p><Link to="/admin">← {t('admin.title')}</Link></p>
      <h1>{t('admin_presences.title')}</h1>
      <p className="muted">{t('admin_presences.intro')}</p>

      {/* Stats above filters + list */}
      <ul className="admin-card-grid" style={{ marginBottom: '1rem' }}>
        <li className="admin-card"><h2>{stats.total_presences ?? 0}</h2><p className="muted">{t('admin_presences.stat_total')}</p></li>
        <li className="admin-card"><h2>{stats.distinct_participants ?? 0}</h2><p className="muted">{t('admin_presences.stat_participants')}</p></li>
        <li className="admin-card"><h2>{stats.distinct_courses ?? 0}</h2><p className="muted">{t('admin_presences.stat_courses')}</p></li>
      </ul>

      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ minWidth: '16rem' }}>
          <span>{t('admin_presences.filter_course')}</span>
          <select value={courseId} onChange={(e) => setCourseId(e.target.value)}>
            <option value="">{t('admin_presences.all_courses')}</option>
            {courses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label style={{ minWidth: '16rem' }}>
          <span>{t('admin_presences.filter_user')}</span>
          <select value={userId} onChange={(e) => setUserId(e.target.value)}>
            <option value="">{t('admin_presences.all_users')}</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
          </select>
        </label>
        <label style={{ maxWidth: '12rem' }}>
          <span>{t('admin_presences.from')}</span>
          <input type="date" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} />
        </label>
        <label style={{ maxWidth: '12rem' }}>
          <span>{t('admin_presences.to')}</span>
          <input type="date" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} />
        </label>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="button" className="link-button" onClick={() => setRange(last30Days())}>
            {t('admin_presences.range_this_month')}
          </button>
          <button type="button" className="link-button" onClick={() => setRange(lastCalendarMonth())}>
            {t('admin_presences.range_last_month')}
          </button>
        </div>
      </div>

      {error && (
        <p className="error">
          {t(`admin_presences.errors.${error}`, { defaultValue: t(`admin.errors.${error}`, { defaultValue: error }) })}
        </p>
      )}
      {loading && <p>{t('common.loading')}</p>}
      {!loading && rows.length === 0 && <p className="muted">{t('admin_presences.empty')}</p>}

      {!loading && rows.length > 0 && (
        <table className="results-table">
          <thead>
            <tr>
              <th>{t('admin_presences.col_date')}</th>
              <th>{t('admin_presences.col_course')}</th>
              <th>{t('admin_presences.col_participant')}</th>
              <th>{t('admin_presences.col_account')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="small">{r.attended_on}</td>
                <td>{r.course_name} <span className="muted small">· {t(`admin_courses.type_${r.course_type}`, { defaultValue: r.course_type })}</span></td>
                <td>{r.participant_name}</td>
                <td>{r.account_name}<br /><span className="muted small">{r.account_email}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  )
}
